import { Box } from '@shopify/polaris';
import { CategoryTemplateView } from './CategoryTemplateView';
import { LockedFeatureCard } from './LockedFeatureCard';
import type { CategoryTemplate, CategoryConfidence } from '../types';

/**
 * T078 [US5] ProductCategorySection — thin wrapper that resolves the active
 * template and renders CategoryTemplateView. Extracted to keep
 * ProductDetailPage under 300 lines.
 */

interface ProductCategorySectionProps {
  readonly hasAccess: boolean;
  readonly detectedCategory: string | null;
  readonly confidence: CategoryConfidence | null;
  readonly categoryOverride: string | null;
  readonly template: CategoryTemplate | null;
  readonly allTemplates: CategoryTemplate[];
  readonly presentAttributes: string[];
  readonly attributeValues?: Record<string, string>;
  readonly attributeValueSources?: Record<string, string>;
  readonly onOverride: (slug: string) => void;
  readonly productId?: string;
}

export function ProductCategorySection({
  hasAccess,
  detectedCategory,
  confidence,
  categoryOverride,
  template,
  allTemplates,
  presentAttributes,
  attributeValues,
  attributeValueSources,
  onOverride,
  productId,
}: ProductCategorySectionProps) {
  if (!hasAccess) {
    return (
      <Box paddingBlockStart="400">
        <LockedFeatureCard
          featureName="Category Templates"
          requiredTier="STARTER"
          description="See category-specific attribute requirements and override the detected category for this product."
          source="product-detail-category-templates"
        />
      </Box>
    );
  }

  const activeSlug = categoryOverride ?? detectedCategory;
  const activeTemplate = template ?? allTemplates.find((t) => t.slug === activeSlug);

  if (!activeTemplate) return null;

  return (
    <Box paddingBlockStart="400">
      <CategoryTemplateView
        template={activeTemplate}
        presentAttributes={presentAttributes}
        attributeValues={attributeValues}
        attributeValueSources={attributeValueSources}
        confidence={confidence}
        allTemplates={allTemplates}
        categoryOverride={categoryOverride}
        detectedCategory={detectedCategory}
        onOverride={onOverride}
        productId={productId}
      />
    </Box>
  );
}
